import React from "react";
import { Button, Divider, Input, Select } from "antd";
import Tooltip from "../Tooltip";
import {
  AlignCenterOutlined,
  BgColorsOutlined,
  FontColorsOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

interface RichTextProps {
  value?: string;
  onChange: (value: string) => void;
  minRows?: number;
  /** Which entry the style list shows. The list is not connected, so this is
   * what the section is written as rather than something the user picks. */
  defaultStyle?: string;
}

/**
 * Where a letter's text is written.
 *
 * The frame is finished — a toolbar over a writing area, sized and spaced the
 * way it will be — but nothing in it is connected: every button is disabled and
 * the area is a plain textarea underneath. Alignment included; it belongs to the
 * same toolbar and comes from the same library, so it waits with the rest rather
 * than being the one control that works.
 *
 * TODO(design): a developer replaces the inside of this file with Quill and
 * connects the whole toolbar — the six marks, the two colour controls and the
 * three alignments. The colour ones need a palette rather than a free picker:
 * a letter has its own colours, and words in a colour outside them will not
 * follow a change of theme. The
 * contract is already the one Quill needs — `value` in, `onChange` out — so no
 * call site has to change. Two things to settle when they do: which package
 * (the original react-quill relies on ReactDOM.findDOMNode, which React 19 has
 * removed, so it cannot be the one), and how the editor's HTML is turned into
 * something an email client will render. See DEVELOPER_TASKS.md.
 */
export default function RichText({
  value,
  onChange,
  minRows = 2,
  defaultStyle = "paragraph",
}: RichTextProps): React.ReactNode {
  const { t } = useTranslation();

  const marks = [
    { key: "bold", icon: <BoldOutlined /> },
    { key: "italic", icon: <ItalicOutlined /> },
    { key: "underline", icon: <UnderlineOutlined /> },
    { key: "link", icon: <LinkOutlined /> },
    { key: "bullet", icon: <UnorderedListOutlined /> },
    { key: "ordered", icon: <OrderedListOutlined /> },
  ];

  // Colouring a run of words is a mark like the others: a whole block's colour
  // is asked for in the panel, and this is for the sentence inside it.
  const colours = [
    { key: "color", icon: <FontColorsOutlined /> },
    { key: "highlight", icon: <BgColorsOutlined /> },
  ];

  // What the text is, before what is done to it: Quill calls this the header
  // format, and it is a list rather than a row of buttons because the six are
  // exclusive.
  const textStyles = ["h1", "h2", "h3", "h4", "paragraph", "caption"];

  // A second group in the same row: alignment is not a mark on a selection, so
  // it reads as its own set of three.
  const alignments = [
    { key: "start", icon: <AlignLeftOutlined /> },
    { key: "center", icon: <AlignCenterOutlined /> },
    { key: "end", icon: <AlignRightOutlined /> },
  ];

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        {/* The tooltip needs an element of its own here: a disabled control
            takes no pointer events, so it would never be hovered. */}
        <Tooltip title={t("common.notConnected")}>
          <span>
            <Select
              className={styles.style}
              size="small"
              disabled
              value={defaultStyle}
              options={textStyles.map((key) => ({
                value: key,
                label: t(`common.textStyles.${key}`),
              }))}
            />
          </span>
        </Tooltip>

        <Divider type="vertical" className={styles.split} />

        {marks.map(({ key, icon }) => (
          // Disabled rather than absent: the row shows what the editor will
          // offer, and a control that looks live but does nothing is worse
          // than one that says it is not ready.
          <Tooltip key={key} title={t("common.notConnected")}>
            <Button type="text" size="small" icon={icon} disabled />
          </Tooltip>
        ))}

        <Divider type="vertical" className={styles.split} />

        {colours.map(({ key, icon }) => (
          <Tooltip key={key} title={t("common.notConnected")}>
            <Button type="text" size="small" icon={icon} disabled />
          </Tooltip>
        ))}

        <Divider type="vertical" className={styles.split} />

        {alignments.map(({ key, icon }) => (
          <Tooltip key={key} title={t("common.notConnected")}>
            <Button type="text" size="small" icon={icon} disabled />
          </Tooltip>
        ))}
      </div>

      <Input.TextArea
        className={styles.area}
        variant="borderless"
        autoSize={{ minRows }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
